import { useSyncExternalStore } from "react"

const QUERY = "(prefers-reduced-motion: reduce)"

function subscribe(callback: () => void): () => void {
  if (typeof window === "undefined" || !window.matchMedia) {
    return () => undefined
  }
  const mql = window.matchMedia(QUERY)
  mql.addEventListener("change", callback)
  return () => mql.removeEventListener("change", callback)
}

function getSnapshot(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false
  return window.matchMedia(QUERY).matches
}

// SSR/initial paint: motion ON (false) until the client store reads the
// real media query — matches the conservative "animate unless told not to".
function getServerSnapshot(): boolean {
  return false
}

/**
 * `usePrefersReducedMotion` — subscribe to the OS "reduce motion"
 * accessibility setting via `matchMedia`, using `useSyncExternalStore`
 * (no setState-in-effect cascade).
 *
 * Returns `true` when the user has requested reduced motion. Consumers
 * use it to drop decorative animation (e.g. OKS-397 `<RankUpModal>`
 * suppresses its entrance pop + confetti, keeping the content + a plain
 * fade). The `motion-safe:`/`motion-reduce:` Tailwind variants cover the
 * CSS side; this hook covers the React side where we conditionally
 * render DOM (the confetti particles are NOT mounted under reduced
 * motion, per UX Spec §4).
 *
 * SSR-safe via `getServerSnapshot` (defaults to `false`); guards
 * `window`/`matchMedia` absence in every branch.
 */
export function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}
